import { Link } from "react-router-dom";
import type { Category, DirectoryLocation } from "@/types/directory";
import SearchBar from "@/components/feature/SearchBar";
import { heroStats, popularSearches } from "@/mocks/siteContent";
import useReveal from "@/hooks/useReveal";
import EmergencyPanel from "./EmergencyPanel";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Photorealistic%20coastline%20of%20Coastal%20Karnataka%20viewed%20from%20above%20with%20vivid%20turquoise%20Arabian%20Sea%20water%2C%20deep%20blue%20sky%20with%20soft%20white%20clouds%2C%20palm%20fringed%20golden%20sand%20beach%2C%20rocky%20headlands%20and%20coconut%20trees%2C%20bright%20clear%20daylight%2C%20saturated%20natural%20colors%2C%20crisp%20high%20detail%20travel%20photography&width=1600&height=900&seq=cbc-home-hero-coastal-02&orientation=landscape";

interface HeroSectionProps {
  categories: Category[];
  districts: DirectoryLocation[];
}

export default function HeroSection({ categories, districts }: HeroSectionProps) {
  const statsReveal = useReveal<HTMLDivElement>(0.1);

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Photo-realistic view of the Coastal Karnataka shoreline with blue sky and the turquoise Arabian Sea"
          title="Discover coastal Karnataka businesses - Coastal Bizz Connect"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60" />
      </div>

      <div className="relative mx-auto w-full max-w-[1400px] px-6 pb-12 pt-20 md:pb-16 md:pt-24">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_330px] lg:items-stretch">
          <div className="flex flex-col items-center justify-center text-center">
            <span className="animate-fade-up inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-background-50 backdrop-blur">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-400">
                <i className="ri-verified-badge-fill text-[13px]" />
              </span>
              4,000+ listed businesses across the Canara coast
            </span>

            <h1 className="animate-fade-up mt-6 max-w-4xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              Discover the best local businesses across coastal Karnataka
            </h1>
            <p className="animate-fade-up mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
              Your trusted directory for South Canara, Udupi and North Canara. Find verified shops,
              restaurants, hotels, hospitals and services — with real reviews, working hours and
              direct contact in one tap.
            </p>

            <div className="mt-8 w-full max-w-3xl">
              <SearchBar categories={categories} districts={districts} />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs font-medium text-background-200">Popular:</span>
              {popularSearches.slice(0, 6).map((term) => (
                <Link
                  key={term}
                  to={`/directory?keyword=${encodeURIComponent(term)}`}
                  className="whitespace-nowrap rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 text-xs font-medium text-background-100 backdrop-blur transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  {term}
                </Link>
              ))}
            </div>

            <div ref={statsReveal.ref} className="mt-12 grid w-full max-w-3xl grid-cols-2 gap-3 md:grid-cols-4">
              {heroStats.map((stat, index) => (
                <div
                  key={stat.id}
                  className={`flex flex-col items-center gap-1 rounded-lg border border-background-50/15 bg-background-50/10 px-4 py-5 backdrop-blur ${
                    statsReveal.visible ? "animate-fade-up" : "opacity-0"
                  }`}
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className={`${stat.icon} text-base`} />
                  </span>
                  <span className="mt-1 font-heading text-xl font-extrabold text-background-50 md:text-2xl">
                    {stat.value}
                  </span>
                  <span className="text-xs text-background-200">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <EmergencyPanel />
        </div>
      </div>
    </section>
  );
}